// src/utils/fetchInterceptor.ts
// Global fetch interceptor that catches 401s on GraphQL calls and
// attempts token refresh + retry. Installed once at app startup so
// all 194+ raw fetch() calls get automatic refresh handling.
//
// This is the one place a refused GraphQL request can sign the tab out, and
// only when the server has really refused the session: the refresh token was
// rejected, or the request was refused again with a freshly refreshed token.
// Anything else (no network, a 5xx, a refresh that couldn't reach the server)
// keeps the user signed in and shows a connection notice.

import {
  refreshAccessToken,
  isRefreshRejected,
  redirectToLogin,
  isRedirectingToLogin
} from '../services/authService';
import { notifyConnectionProblem } from '../services/sessionEvents';

const originalFetch = window.fetch;

window.fetch = async function interceptedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  if (!url.includes('/api/graphql')) {
    return originalFetch.call(window, input, init);
  }

  let response: Response;
  try {
    response = await originalFetch.call(window, input, init);
  } catch (error) {
    notifyConnectionProblem();
    throw error;
  }

  if (response.status >= 500) {
    notifyConnectionProblem();
    return response;
  }

  if (response.status !== 401 || isRedirectingToLogin()) {
    return response;
  }

  let newToken: string;
  try {
    newToken = await refreshAccessToken();
  } catch (error) {
    if (isRefreshRejected(error)) {
      redirectToLogin('refresh_rejected', 401);
    } else {
      notifyConnectionProblem();
    }
    return response;
  }

  // Rebuild the request with the new token
  const retryHeaders = new Headers(init?.headers || {});
  retryHeaders.set('Authorization', `Bearer ${newToken}`);

  const retry = await originalFetch.call(window, input, {
    ...init,
    headers: retryHeaders
  });

  if (retry.status === 401) {
    redirectToLogin('retry_unauthorized', 401);
  }
  return retry;
};
